/** @jsx jsx */
import { PrimitiveProps } from "@styles/types";
import { Box, jsx, ResponsiveStyleValue } from "theme-ui";
import { ImageProcessorParams } from "../../../../carlsberg-headless/image-processor/serialize-query-parameters";
import { ImageField } from "../../../../carlsberg-headless/types/fields/Image";
import React, { useEffect, useState, PropsWithChildren } from "react";
import { motion, MotionValue, useTransform, useScroll } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  BackgroundImageStyles,
  ContentWrapperStyles,
  ImageStyles,
} from "./styles";
import { UmbracoImage } from "../UmbracoImage/UmbracoImage";
import { useNavigationContext } from "../../../../context/navigation/useNavigationContext";

export interface UmbracoParallaxImageProps extends PrimitiveProps {
  params?: ResponsiveStyleValue<ImageProcessorParams>;
  image: ImageField;
  width?: ResponsiveStyleValue<string | number>;
  height?: ResponsiveStyleValue<string | number>;
  lazyLoadOffset?: number;
  parallaxY: number;
}

const UmbracoParallaxBackgroundImage: React.FC<
  PropsWithChildren<UmbracoParallaxImageProps>
> = ({ sx, children, parallaxY, image, ...otherProps }) => {
  const { isOpen } = useNavigationContext();
  const { scrollY } = useScroll();
  const [ref, inView, entry] = useInView({
    triggerOnce: false,
  });
  const y = useTransform(
    scrollY,
    [0, entry?.boundingClientRect?.height ?? 0],
    [0, parallaxY]
  );
  const [cachedY, setCachedY] = useState<MotionValue<number>>(y);

  useEffect(() => {
    setCachedY(y);
  }, [isOpen, y]);

  if (!image) {
    return null;
  }

  const { altText } = image;

  return (
    <Box ref={ref} sx={{ ...BackgroundImageStyles, ...sx }}>
      <motion.div
        ref={ref}
        sx={{
          ...BackgroundImageStyles,
          ...(isOpen && {
            transform: `translate3d(0, ${cachedY.get()}px, 0) !important`, //overwrite motion value when nav menu is open to avoid reset
          }),
        }}
        {...(inView && { style: { y } })}
      >
        <UmbracoImage
          isCover
          alt={altText ?? ""}
          image={image}
          {...otherProps}
          sx={{
            ...ImageStyles,
          }}
        />
      </motion.div>
      <Box sx={ContentWrapperStyles}>{children}</Box>
    </Box>
  );
};

export { UmbracoParallaxBackgroundImage };
